import React, { useState } from "react";
import { ConfigProvider, Form, Input, Button, Select, Flex, Modal, message } from "antd";
import { BusinessType } from "shared/types/enum";
import { RegisterBusinessForm } from "shared/types";
import { translate } from "shared/languages";
import { PlusOutlined } from "@ant-design/icons";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { useRegisterBusiness } from "hooks/mutations/business";
import PhoneInput, { phoneRules } from "shared/components/PhoneInput";

const { Option } = Select;

const RegisterBusinessModal: React.FC = () => {
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: registerBusiness, isPending } = useRegisterBusiness();

  const handleCancel = () => {
    form.resetFields();
    setIsModalOpen(false);
  };

  const onFinish = (values: RegisterBusinessForm) => {
    registerBusiness(values, {
      onSuccess: () => {
        handleCancel();
      },
      onError: (e) => {
        console.log("[RegisterBusiness] Error: ", e);
        message.error("Business Registration Failed");
        handleCancel();
      },
    });
  };

  return (
    <>
      <Button onClick={() => setIsModalOpen(true)} type="primary" icon={<PlusOutlined />}>
        {translate("button.add")} {translate("business")}
      </Button>
      <ConfigProvider theme={modalButtonsTheme}>
      <Modal
        title={`${translate("button.add")} ${translate("business")}`}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form form={form} name="register-business" onFinish={onFinish} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={translate("label.name")}
            rules={[{ required: true, message: "Please input your business name!" }]}
          >
            <Input placeholder="Enter business name" />
          </Form.Item>

          <Form.Item name="legal_name" label={translate("label.legal_name")}>
            <Input placeholder="PT ..." />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: "email", message: "The input is not a valid email!" },
              { required: true, message: "Please input your email!" },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="phone_number"
            label={translate("label.phone_number")}
            rules={phoneRules()}
          >
            <PhoneInput placeholder={translate("form.placeholders.phone_number")} />
          </Form.Item>

          <Form.Item
            name="address"
            label={translate("label.address")}
            rules={[{ required: true, message: "Please input your address!" }]}
          >
            <Input.TextArea placeholder="Enter business address" />
          </Form.Item>

          <Form.Item
            name="business_type"
            label={translate("label.business_type")}
            rules={[{ required: true, message: "Please select your business type!" }]}
          >
            <Select placeholder="Select business type">
              {Object.keys(BusinessType).map((business_type) => {
                return (
                  <Option key={business_type} value={business_type}>
                    {business_type.toUpperCase()}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Flex gap={12} justify="flex-end">
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={isPending}>
                {translate("button.add")}
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Modal>
      </ConfigProvider>
    </>
  );
};

export default RegisterBusinessModal;
